export interface TagProcessorConfig {
    /**
     * Tags where the content should be preserved but the tags removed.
     * Example: <escape>content</escape> becomes "content"
     */
    contentPreservingTags: string[];

    /**
     * Tags where both the tags and content should be removed.
     * Example: <translation-context>content</translation-context> becomes ""
     */
    contentRemovingTags: string[];

    /**
     * Whether to enable logging for debugging.
     */
    enableLogging?: boolean;
}

/**
 * Post-processor plugin for i18next that removes specified HTML-like tags from translations.
 *
 * This processor handles two types of tags:
 * 1. Content-preserving tags (like <escape>) - removes tags but keeps content
 * 2. Content-removing tags (like <translation-context>) - removes tags and content
 *
 * @example
 * // Input: "Hello <escape>world</escape>"
 * // Output: "Hello world"
 * // Input: "<translation-context>To refuse or reject something</translation-context>Decline"
 * // Output: "Decline"
 */
class TranslationTagProcessor {
    private config: TagProcessorConfig;

    constructor(config: TagProcessorConfig) {
        this.config = {
            enableLogging: false,
            ...config,
        };

        if (this.config.enableLogging) {
            // eslint-disable-next-line no-console
            console.log('[TranslationTagProcessor] Initialized with config:', this.config);
        }
    }

    /**
     * Process a translation string by removing configured tags.
     *
     * @param value - The translation string to process
     * @param key - The translation key (for logging)
     * @returns The processed string with tags removed
     */
    process(value: string, key?: string): string {
        if (!value || typeof value !== 'string') {
            return value;
        }

        let processedValue = value;
        let hasChanges = false;

        // Process content-preserving tags (remove tags, keep content)
        this.config.contentPreservingTags.forEach((tag) => {
            const regex = TranslationTagProcessor.createTagRegex(tag);
            const matches = processedValue.match(regex);

            if (matches) {
                hasChanges = true;
                processedValue = processedValue.replace(regex, '$1');

                if (this.config.enableLogging) {
                    // eslint-disable-next-line no-console
                    console.log(`[TranslationTagProcessor] Removed content-preserving tag '${tag}' from key '${key}'`);
                }
            }
        });

        // Process content-removing tags (remove tags and content)
        this.config.contentRemovingTags.forEach((tag) => {
            const regex = TranslationTagProcessor.createTagRegex(tag);
            const matches = processedValue.match(regex);

            if (matches) {
                hasChanges = true;
                processedValue = processedValue.replace(regex, '');

                if (this.config.enableLogging) {
                    // eslint-disable-next-line no-console
                    console.log(
                        `[TranslationTagProcessor] Removed content-removing tag '${tag}' and its content from key '${key}'`,
                    );
                }
            }
        });

        // Clean up any extra whitespace that might result from tag removal
        if (hasChanges) {
            processedValue = TranslationTagProcessor.cleanupWhitespace(processedValue);

            if (this.config.enableLogging) {
                // eslint-disable-next-line no-console
                console.log(`[TranslationTagProcessor] Original: "${value}"`);
                // eslint-disable-next-line no-console
                console.log(`[TranslationTagProcessor] Processed: "${processedValue}"`);
            }
        }

        return processedValue;
    }

    /**
     * Returns a regex pattern for matching opening and closing tags.
     *
     * @param tagName - The tag name (without < >)
     * @returns RegExp for matching the tag and its content
     */
    private static createTagRegex(tagName: string): RegExp {
        // Escape any special regex characters in the tag name
        const escapedTagName = tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        // Create regex that matches: <tag>content</tag> or <tag-name>content</tag-name>
        // Capture group 1 contains the content between tags
        return new RegExp(`<${escapedTagName}>(.*?)</${escapedTagName}>`, 'gi');
    }

    /**
     * Cleans up extra whitespace that might result from removing tags.
     *
     * @param text - The text to clean up
     * @returns Cleaned text
     */
    private static cleanupWhitespace(text: string): string {
        return text
            .replace(/\s+/g, ' ') // Replace multiple consecutive spaces with single space
            .trim();
    }

    /**
     * Updates the processor configuration.
     *
     * @param newConfig - New configuration to merge with existing
     */
    updateConfig(newConfig: Partial<TagProcessorConfig>): void {
        this.config = { ...this.config, ...newConfig };

        if (this.config.enableLogging) {
            // eslint-disable-next-line no-console
            console.log('[TranslationTagProcessor] Configuration updated:', this.config);
        }
    }

    /**
     * Returns the current configuration.
     *
     * @returns Current processor configuration
     */
    getConfig(): TagProcessorConfig {
        return { ...this.config };
    }
}

/**
 * Factory function to create the i18next post-processor plugin.
 *
 * @param config - Configuration for the tag processor
 * @returns i18next post-processor plugin object
 */
export function createTranslationTagProcessorPlugin(config: TagProcessorConfig): {
    type: 'postProcessor';
    name: string;
    process: (value: string, key: string) => string;
    getProcessor: () => TranslationTagProcessor;
} {
    const processor = new TranslationTagProcessor(config);

    const plugin = {
        type: 'postProcessor' as const,
        name: 'translationTagProcessor',
        process: (value: string, key: string) => processor.process(value, key),
        // Expose processor instance for configuration updates
        getProcessor: () => processor,
    };

    return plugin;
}

export default TranslationTagProcessor;
